import { FilterOption, filter } from '@zspace/data';
import { formatSalesOrderNumber } from '@zspace/format';
import { userHasPermissionToViewDeviceDetails } from '@zspace/roles';
import {
  Criteria,
  Device,
  PaginatedContentConfig,
  SortDirection,
} from '@zspace/types';
import { useCallback, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaCheck } from 'react-icons/fa6';
import { PiWarningFill } from 'react-icons/pi';
import Conditional from '../../shared/conditional/conditional';
import useIsCustomerUser from '../../shared/hooks/is-customer-user';
import useSalesOrderPermissions from '../../shared/hooks/sales-order-permissions';
import useUser from '../../shared/hooks/user';
import { removeDuplicates } from '../../shared/utils';
import Link from '../../ui/link/link';
import PaginatedTable from '../../ui/table/paginated-table/paginated-table';
import { Column, OnTableChangeData, RowSelection } from '../../ui/table/types';
import { getDeviceName } from '../utils';

const NO_SOFTWARE_ASSIGNED = 'No software assigned yet';

const filterOptions: FilterOption<Device>[] = [
  {
    getter: (el) => el.serialNumber?.number ?? el.name ?? el.defaultName,
    searchable: true,
  },
  {
    getter: (el) => formatSalesOrderNumber(el.salesOrderLine.salesOrder.number),
    searchable: true,
  },
  {
    getter: (el) => el.name ?? el.defaultName,
    searchable: true,
  },
  {
    getter: (el) => el.model.displayName,
    searchable: true,
  },
  {
    getter: (el) =>
      el.assignments
        .filter((assignment) => assignment.softwareSeat)
        .map(
          (assignment) =>
            assignment.softwareSeat.licensingProductGroup.displayName
        )
        .join(', '),
    searchable: true,
  },
];

export type DeviceGroupTableProps = {
  data: Device[];
  selectedDevices?: Device[];
  selectedSoftware?: string[];
  onManageDeviceClick?: (devices: Device[], add: boolean) => void;
  enableSearch?: boolean;
  isInvalidSelection?: (devices: Device[]) => boolean;
  showCompleted?: boolean;
  isDataFiltered?: boolean;
};

const INITIAL_ITEMS_PER_PAGE = 50;

export function DeviceGroupTable({
  data,
  selectedDevices,
  onManageDeviceClick,
  selectedSoftware = [],
  enableSearch = true,
  isInvalidSelection = () => false,
  showCompleted = false,
  isDataFiltered = false,
}: DeviceGroupTableProps) {
  const { user } = useUser();
  const isCustomerUser = useIsCustomerUser();

  const {
    userHasSalesOrderManagePermissions,
    userIsJustViewOnlySalesOrderManager,
  } = useSalesOrderPermissions();

  const [tableCriteria, setTableCriteria] = useState<Criteria>({
    itemsPerPage: INITIAL_ITEMS_PER_PAGE,
    pageNumber: 1,
    search: '',
    sortBy: 'salesOrderLine.salesOrder.number',
    sortDirection: SortDirection.DES,
  });

  const tableData = useMemo(() => {
    // Filter by table columns
    const columnFilteredData = filter(data, tableCriteria, filterOptions);
    if (isCustomerUser && !userIsJustViewOnlySalesOrderManager) {
      return columnFilteredData.sort((deviceA, deviceB) => {
        const canManageDeviceA = userHasSalesOrderManagePermissions(
          deviceA.salesOrderLine.salesOrder.id
        );
        const canManageDeviceB = userHasSalesOrderManagePermissions(
          deviceB.salesOrderLine.salesOrder.id
        );
        if (canManageDeviceA && !canManageDeviceB) return -1;
        if (canManageDeviceA && canManageDeviceB) return 0;
        return 1;
      });
    }
    return columnFilteredData;
  }, [
    data,
    isCustomerUser,
    tableCriteria,
    userHasSalesOrderManagePermissions,
    userIsJustViewOnlySalesOrderManager,
  ]);

  const tablePageData = useMemo(() => {
    return tableData.slice(
      (tableCriteria.pageNumber - 1) * tableCriteria.itemsPerPage,
      tableCriteria.pageNumber * tableCriteria.itemsPerPage
    );
  }, [tableCriteria.itemsPerPage, tableCriteria.pageNumber, tableData]);

  const isDisabledRow = useCallback(
    (row: Device) => {
      if (isCustomerUser && !userIsJustViewOnlySalesOrderManager) {
        return !userHasSalesOrderManagePermissions(
          row.salesOrderLine.salesOrder.id
        );
      }
      return false;
    },
    [
      isCustomerUser,
      userHasSalesOrderManagePermissions,
      userIsJustViewOnlySalesOrderManager,
    ]
  );

  const tableForm = useMemo(
    () => ({
      debounceMillis: 0,
      enableSearch,
    }),

    [enableSearch]
  );

  const tableConfig = useMemo(() => {
    return {
      ...tableCriteria,
      pages: Math.ceil(tableData.length / tableCriteria.itemsPerPage),
    };
  }, [tableCriteria, tableData.length]);

  const columns: Column<Device>[] = useMemo(
    () => [
      {
        key: 'serialNumber.number',
        className: 'is-width-20',
        render: (el) => {
          const showError = isInvalidSelection([el]);
          let deviceIdentifier = el.serialNumber?.number;
          if (!deviceIdentifier) {
            deviceIdentifier = el.name ?? el.defaultName;
          }
          return (
            <>
              {showError && (
                <PiWarningFill
                  className="has-text-danger-dark"
                  style={{ verticalAlign: 'text-bottom' }}
                />
              )}
              <Conditional
                condition={userHasPermissionToViewDeviceDetails(
                  user,
                  el.salesOrderLine.salesOrder.id
                )}
              >
                <Conditional.True>
                  <Link to={`/devices/${el.id}`}>{deviceIdentifier}</Link>
                </Conditional.True>
                <Conditional.False>
                  <div>{deviceIdentifier}</div>
                </Conditional.False>
              </Conditional>
              {showCompleted &&
                el.assignments.some(
                  (assignment) => assignment.softwareSeat
                ) && (
                  <Icon className="has-text-success-dark">
                    <FaCheck />
                  </Icon>
                )}
            </>
          );
        },
        title: 'Identifier',
        sortable: false,
        noWrap: true,
      },
      {
        key: 'salesOrderLine.salesOrder.number',
        className: 'is-width-15',
        render: (el) => (
          <Conditional condition={isDisabledRow(el)}>
            <Conditional.True>
              <span className="is-underlined">
                {formatSalesOrderNumber(el.salesOrderLine.salesOrder.number)}
              </span>
            </Conditional.True>
            <Conditional.False>
              <Link to={`/sales-orders/${el.salesOrderLine.salesOrder.id}`}>
                {formatSalesOrderNumber(el.salesOrderLine.salesOrder.number)}
              </Link>
            </Conditional.False>
          </Conditional>
        ),
        title: 'Sales order',
        sortable: true,
      },
      {
        key: 'name',
        className: 'is-width-15',
        render: (el) => getDeviceName(el),
        title: 'Device name',
        sortable: false,
      },
      {
        key: 'model.name',
        className: 'is-width-20',
        render: (el) => el.model.displayName,
        title: 'Device type',
        noWrap: true,
      },
      {
        key: 'software',
        className: 'is-width-25',
        render: (el) => {
          const assignedSoftwareTitles = el.assignments
            .filter((assignment) => assignment.softwareSeat)
            .map(
              (assignment) =>
                assignment.softwareSeat.licensingProductGroup.displayName
            );
          if (
            assignedSoftwareTitles.length === 0 &&
            selectedSoftware.length === 0
          )
            return NO_SOFTWARE_ASSIGNED;
          return removeDuplicates([
            ...selectedSoftware,
            ...assignedSoftwareTitles,
          ]).join(', ');
        },
        title: 'Assigned software',
      },
    ],
    [isDisabledRow, isInvalidSelection, selectedSoftware, showCompleted, user]
  );

  const rowSelection: RowSelection | undefined = useMemo(() => {
    let hasDevicesManagerPermission = true;
    if (isCustomerUser && !userIsJustViewOnlySalesOrderManager) {
      hasDevicesManagerPermission = tableData.some((device) =>
        userHasSalesOrderManagePermissions(device.salesOrderLine.salesOrder.id)
      );
    }
    if (selectedDevices && onManageDeviceClick && hasDevicesManagerPermission) {
      return {
        onChange: (selectedRowIds) => {
          selectedRowIds.forEach((id) => {
            const foundRow = selectedDevices.find((sd) => sd.id === id);

            if (foundRow) {
              onManageDeviceClick([foundRow], false);
            } else {
              const newSelectedDevice =
                tableData.find((device) => device.id === id) || ({} as Device);
              onManageDeviceClick([newSelectedDevice], true);
            }
          });
        },
        selectedRowKeys: tableData
          .filter((tableDataDevice) =>
            selectedDevices
              .map((selectedDevice) => selectedDevice.id)
              .includes(tableDataDevice.id)
          )
          .map((d) => d.id),
      };
    }
  }, [
    isCustomerUser,
    onManageDeviceClick,
    selectedDevices,
    tableData,
    userHasSalesOrderManagePermissions,
    userIsJustViewOnlySalesOrderManager,
  ]);

  const handleTableChange = (value: OnTableChangeData<Device>) => {
    const newTableCriteria: PaginatedContentConfig = {
      ...tableCriteria,
      ...value.config,
      sortBy: value.column?.key || tableCriteria.sortBy,
    };

    setTableCriteria(newTableCriteria);
  };

  const emptyContent = useMemo(() => {
    const emptyContentText =
      isDataFiltered || tableCriteria.search !== ''
        ? 'There are no devices that match the selected criteria'
        : 'There are no devices at the moment';
    return (
      <Element display="flex" justifyContent="center" className="my-2">
        <span className="has-text-weight-light">{emptyContentText}</span>
      </Element>
    );
  }, [isDataFiltered, tableCriteria.search]);

  return (
    <PaginatedTable
      form={tableForm}
      dataSource={tablePageData}
      columns={columns}
      onChange={handleTableChange}
      config={tableConfig}
      rowKey={'id'}
      rowSelection={rowSelection}
      isInvalidSelection={isInvalidSelection}
      isDisabledSelection={isDisabledRow}
      empty={emptyContent}
    />
  );
}

export default DeviceGroupTable;
